import type { BadgeVariant } from '@/components/ui/Badge';
import { TEAM_STRINGS } from '@/lib/constants/team-strings';
import type { AccountStatus, TeamMember } from './api/team.api';

/**
 * Single, precomputed read of what a member's `accountStatus` (the USER
 * ACCOUNT status - distinct from the membership `status`, see the comment on
 * `TeamMember.accountStatus`) allows in the Team Members table: whether the
 * row shows a status badge, and the reason a disabled control carries.
 *
 * Computed once per row and read off by every column that cares (the member
 * badge, both permission toggles, the role-change link) instead of each one
 * re-testing `accountStatus` separately - mnr-standards' "no repeated
 * ternary/if-chains on one discriminant" rule, same shape as
 * `getConfirmDialogConfig` in `TeamPermissionsPage.tsx`.
 */
export interface MemberAccountAccess {
  /** False for any non-'active' accountStatus (suspended/deactivated/deleted). */
  isActive: boolean;
  /** Row badge for a non-active account; null when active (no badge). */
  badge: { label: string; variant: BadgeVariant } | null;
  /** Short, factual reason a disabled control carries; '' when active. */
  disabledReason: string;
}

const BADGE_VARIANT: Record<Exclude<AccountStatus, 'active'>, BadgeVariant> = {
  suspended: 'danger',
  deactivated: 'neutral',
  deleted: 'neutral',
};

export function getMemberAccountAccess(member: TeamMember): MemberAccountAccess {
  if (member.accountStatus === 'active') {
    return { isActive: true, badge: null, disabledReason: '' };
  }
  const { accountStatus } = member;
  return {
    isActive: false,
    badge: {
      label: TEAM_STRINGS.MEMBER_ACCOUNT_STATUS.BADGE_LABEL[accountStatus],
      variant: BADGE_VARIANT[accountStatus],
    },
    disabledReason: TEAM_STRINGS.MEMBER_ACCOUNT_STATUS.DISABLED_REASON[accountStatus],
  };
}
